import React, { useState, useEffect } from 'react';

import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import dayjs from 'dayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';

import CustomFormTitle from './CustomFormTitle';
import styled from 'styled-components';
import { FormDateRangePickerInterface } from '../../Models/Common/FormFieldComponentInterfaces';
import { IconCalendar } from '@tabler/icons-react';
import { IconButton } from '@mui/material';

import LocalStorageHelper from '../../Utilities/Helpers/LocalStorageHelper';
import DateHelper from "../../Utilities/Helpers/DateHelper";

import { DateViewTypeEnum } from '../../Utilities/Enums/DateViewTypeEnum';

const StyledDateRangePickerContainer = styled.div<{ $isdarkmode: boolean; $disabled: boolean; }>`
    width: 100%;

    .MuiInputBase-root {
        font-size: 0.9rem !important;
        color: ${props => props.theme.primaryText};
        width: auto;
        display: flex;
        justify-content: space-between;

        &::placeholder {
            color: var(--fieldFocusedBorderPrimaryColor);
            opacity: 0.8;
        }

        .Mui-disabled {
            // background-color: ${(props) => (props.theme.disabled)};
            background-color: ${(props) => (props.$isdarkmode ? '#242728' : '#e9ebec')};
        }
    }

    .MuiOutlinedInput-root {
        padding-right: 5px !important;
    }

    .MuiTextField-root {
        margin-top: 0px !important;
        min-width: unset !important;
        // background: ${props => props.theme.fieldBackgroundColor};
        background-color: ${(props) => (!props.$disabled ? props.theme.fieldBackgroundColor : (props.$isdarkmode ? '#242728' : '#e9ebec'))};
    }

    .MuiInputBase-input {
        padding:  7px 8px !important; //text padding
        width: auto;
    }

    .MuiFormHelperText-root {
        margin-left: 0px !important; //helper margin
    }

    .MuiIconButton-root {
        margin-right: 0px !important;
    }

    .MuiStack-root {
        padding-top: 0px !important; //distance to title
    }
`;

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Asia/Kuala_Lumpur");

const FormDateRangePicker = (fieldProps: FormDateRangePickerInterface) => {
    let isDarkMode = LocalStorageHelper.isDarkMode();

    //States
    const [open, setOpen] = useState(false)
    const [pickerKey, setPickerKey] = React.useState(0); //this will be use to force to rerender the daterange picker to fix

    useEffect(() => {
        setPickerKey(prevKey => prevKey + 1);
    }, [fieldProps.fieldValue]);

    const renderDateRangePickerUI = () => {
        const errorMsg = (fieldProps.errorMessages ? fieldProps.errorMessages[fieldProps.name] : null) ?? null;

        let rangeValues1: any = fieldProps?.fieldValue && fieldProps.fieldValue.length > 0 && fieldProps.fieldValue[0] ? dayjs(new Date(fieldProps.fieldValue[0])) : null;
        let rangeValues2: any = fieldProps?.fieldValue && fieldProps.fieldValue.length > 0 && fieldProps.fieldValue[1] ? dayjs(new Date(fieldProps.fieldValue[1])) : null;

        return <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={['SingleInputDateRangeField']}>
                <DateRangePicker {...{
                    key: pickerKey, // Apply the key
                    enableAccessibleFieldDOMStructure: false,
                    calendars: fieldProps.calendarCount,
                    slots: { field: SingleInputDateRangeField },
                    name: fieldProps.name,
                    defaultValue: [rangeValues1, rangeValues2],
                    disabled: fieldProps.disabled,
                    disablePast: fieldProps.disablePast,
                    disableFuture: fieldProps.disableFuture,
                    format: (fieldProps.outputDateFormat != null && fieldProps.outputDateFormat != "" ? fieldProps.outputDateFormat : "DD/MM/YYYY"),
                    onAccept: (newValue: any) => {
                        if (fieldProps.handleOnChange && newValue[0] && newValue[1] && (!isNaN(Date.parse(newValue[0]))) && (!isNaN(Date.parse(newValue[1])))) {
                            let from = newValue[0];
                            let to = from > newValue[1] ? from : newValue[1];

                            // fieldProps.handleOnChange([dayjs(from), dayjs(to)]);
                            // fieldProps.handleOnChange([DateHelper.getFormattedDate(from, "yyyy-MM-dd"), DateHelper.getFormattedDate(to, "yyyy-MM-dd")]);
                            fieldProps.handleOnChange([
                                (from ? DateHelper.getFormattedDate(from, "yyyy-MM-dd") : null),
                                (to ? DateHelper.getFormattedDate(to, "yyyy-MM-dd") : null)
                            ]);
                        }
                    },
                    open: open,
                    onClose: () => { setOpen(false); },
                    slotProps: {
                        field: { clearable: true },
                        ...{
                            textField: {
                                error: errorMsg != null && errorMsg != "",
                                helperText: errorMsg,
                                placeholder: fieldProps.placeholder ?? '',
                                InputProps: {
                                    ...{
                                        endAdornment: <IconButton
                                            disabled={fieldProps.disabled}
                                            onClick={() => setOpen((prev) => !prev)}>
                                            <IconCalendar size={18} />
                                        </IconButton>
                                    }
                                },
                                onKeyDown: (e: any) => {
                                    let key: any = e.which || e.keyCode || e.charCode;

                                    if (key === 8 && fieldProps?.handleOnChange) {
                                        fieldProps.handleOnChange([null, null]);
                                    }
                                },
                            },
                        }
                    },
                }} />
            </DemoContainer>
        </LocalizationProvider>
    }

    return <StyledDateRangePickerContainer $isdarkmode={isDarkMode} $disabled={fieldProps?.disabled ?? false} className="customDatePicker">
        {/* ---------TITLE---------- */}
        {!fieldProps.hideLabel && <CustomFormTitle title={fieldProps.title}
            required={fieldProps.required}
            hideLabel={fieldProps.hideLabel}
            exclamationHoverItemDisplayText={fieldProps.exclamationHoverItemDisplayText} />}
        {/* ---------DATE PICKER---------- */}
        {renderDateRangePickerUI()}
    </StyledDateRangePickerContainer>
}

export default FormDateRangePicker;